<div class="space-y-6">
  <app-page-header [backTo]="['/owners']" backLabel="Retour à la liste" />

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      (retry)="fetch()"
    />
  } @else if (owner(); as o) {
    <!-- Identity card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0 flex-1">
          <h1 class="text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">
            {{ displayName() }}
          </h1>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            @if (isCompany()) {
              <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">Société</span>
            } @else {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">Particulier</span>
            }
            @if (o.instantBooking) {
              <span class="inline-flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-medium text-success-700 dark:bg-success-500/15 dark:text-success-400">
                Réservation immédiate
              </span>
            }
          </div>
          <div class="mt-4">
            <app-user-cell [user]="o.user" [fallbackId]="o.userId" size="md" />
          </div>
        </div>
        <a
          [routerLink]="['/users', o.userId]"
          class="inline-flex items-center gap-2 self-start rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
        >
          Voir le compte utilisateur
        </a>
      </div>
    </section>

    <!-- Statistiques -->
    <section>
      <h2 class="mb-3 text-sm font-semibold text-gray-800 dark:text-white/90">Statistiques</h2>
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
          <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Véhicules</p>
          <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">{{ o.totalVehicles ?? 0 }}</p>
        </div>
        <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
          <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Locations</p>
          <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">{{ o.totalRentals ?? 0 }}</p>
        </div>
        <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
          <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Note moyenne</p>
          <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
            @if (o.averageRating != null) {
              {{ o.averageRating | number:'1.1-1' }} <span class="text-base font-normal text-gray-400">/ 5</span>
            } @else { — }
          </p>
        </div>
        <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
          <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Revenus totaux</p>
          <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
            {{ (o.totalEarnings ?? 0) | number:'1.0-0' }}
            <span class="text-sm font-normal text-gray-400">XOF</span>
          </p>
        </div>
        <div
          class="rounded-2xl border bg-white p-4 shadow-theme-xs dark:bg-white/[0.03]"
          [class.border-error-200]="+(o.debt ?? 0) > 0"
          [class.dark:border-error-500/30]="+(o.debt ?? 0) > 0"
          [class.border-gray-200]="+(o.debt ?? 0) === 0"
          [class.dark:border-gray-800]="+(o.debt ?? 0) === 0"
        >
          <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Dette CREDIT</p>
          <p
            class="mt-1 text-2xl font-bold"
            [class.text-error-600]="+(o.debt ?? 0) > 0"
            [class.dark:text-error-400]="+(o.debt ?? 0) > 0"
            [class.text-gray-800]="+(o.debt ?? 0) === 0"
            [class.dark:text-white/90]="+(o.debt ?? 0) === 0"
          >
            {{ (o.debt ?? 0) | number:'1.0-0' }}
            <span class="text-sm font-normal text-gray-400">XOF</span>
          </p>
        </div>
      </div>
    </section>

    <!-- Info grid -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      @if (isCompany()) {
        <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
          <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Informations entreprise</h2>
          <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Raison sociale</dt>
              <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ o.businessName ?? '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Email pro</dt>
              <dd class="mt-1 break-all text-sm text-gray-800 dark:text-white/90">{{ o.businessEmail ?? '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Téléphone pro</dt>
              <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ o.businessPhone ?? '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Adresse pro</dt>
              <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ o.businessAddress ?? '—' }}</dd>
            </div>
          </dl>
        </section>
      }

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Préférences</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Réservation immédiate</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ o.instantBooking ? 'Oui' : 'Non' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Temps de réponse</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ o.responseTime ?? '—' }}</dd>
          </div>
        </dl>
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Métadonnées</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">ID</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">#{{ o.id }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Créé le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ o.createdAt ? (o.createdAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Modifié le</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ o.updatedAt ? (o.updatedAt | ldate:'dd MMM yyyy, HH:mm':'':'fr-SN') : '—' }}
            </dd>
          </div>
        </dl>
      </section>
    </div>
  }
</div>
